<template>
  <v-content>
    <v-container>
      <v-card>
        <v-card-title>
          <v-icon class="mr-2">admin_panel_settings</v-icon>
          Perfiles y permisos
          <v-spacer/>
          <v-text-field
            v-model="search"
            append-icon="search"
            label="Buscar"
            single-line
            hide-details
            style="max-width: 250px"
          />
          <v-btn @click="openModal()" class="mx-2 py-0" color="primary">
            <v-icon>add_circle</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pb-0">
          <v-alert type="info" dense text>
            El perfil define a que pantallas entra cada usuario y que puede hacer.
            Se asigna a cada persona desde la pantalla de Usuarios.
          </v-alert>
        </v-card-text>

        <!-- Mientras la coleccion no termino de bindearse no se puede afirmar que
             no haya perfiles: si no, el estado vacio parpadea en cada entrada. -->
        <v-card-text v-if="cargando" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" size="40"/>
          <div class="body-2 grey--text mt-3">Cargando perfiles...</div>
        </v-card-text>

        <!-- Estado inicial: todavia no hay perfiles cargados -->
        <v-card-text v-else-if="perfiles.length === 0" class="text-center py-8">
          <v-icon size="56" color="grey lighten-1">admin_panel_settings</v-icon>
          <div class="subtitle-1 mt-3 mb-1">Todavia no hay perfiles configurados</div>
          <div class="body-2 grey--text mb-4">
            Se pueden crear los tres perfiles sugeridos (Administrador, Auditor y
            Gestor de dependencia) y despues ajustarlos.
          </div>
          <v-btn color="primary" :loading="sembrando" @click="sembrar">
            <v-icon left>auto_fix_high</v-icon>
            Crear perfiles iniciales
          </v-btn>
        </v-card-text>

        <v-data-table
          v-else
          :headers="headers"
          :items="perfiles"
          :search="search"
          :items-per-page="10"
          class="elevation-1"
        >
          <template v-slot:item.nombre="{ item }">
            {{ item.nombre }}
            <v-chip v-if="item.sistema" x-small color="primary" dark class="ml-1">Sistema</v-chip>
          </template>

          <template v-slot:item.pantallas="{ item }">
            <span v-if="item.sistema">Todas</span>
            <v-chip v-else x-small color="info">{{ (item.pantallas || []).length }}</v-chip>
          </template>

          <template v-slot:item.permisos="{ item }">
            <span v-if="item.sistema">Todos</span>
            <v-chip v-else x-small color="info">{{ (item.permisos || []).length }}</v-chip>
          </template>

          <template v-slot:item.usuarios="{ item }">
            <v-chip x-small :color="usuariosConPerfil(item.id) > 0 ? 'success' : 'grey lighten-1'" dark>
              {{ usuariosConPerfil(item.id) }}
            </v-chip>
          </template>

          <template v-slot:item.activo="{ item }">
            <v-chip :color="item.activo !== false ? 'success' : 'error'" dark small>
              {{ item.activo !== false ? 'Activo' : 'Inactivo' }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-icon v-if="item.sistema" small disabled class="mr-2" title="El perfil de sistema no se puede modificar">lock</v-icon>
            <template v-if="!item.sistema">
              <v-icon small class="mr-2" title="Editar" @click="editItem(item)">edit</v-icon>
              <v-icon small title="Eliminar" @click="deleteItem(item)">delete</v-icon>
            </template>
          </template>
        </v-data-table>
      </v-card>

      <!-- Crear / editar -->
      <v-dialog v-model="dialog" width="920" scrollable persistent>
        <v-card class="dialogo-perfil">
          <v-card-title class="dialogo-encabezado">
            <v-icon dark class="mr-3">{{ edit ? 'edit' : 'add_circle_outline' }}</v-icon>
            <div>
              <div class="dialogo-titulo">{{ edit ? 'Editar perfil' : 'Nuevo perfil' }}</div>
              <div class="dialogo-subtitulo">
                Define a que pantallas entra y que puede hacer en cada una
              </div>
            </div>
          </v-card-title>

          <v-card-text class="dialogo-cuerpo">
            <ValidationObserver ref="obs" tag="div">

              <!-- Datos del perfil -->
              <div class="bloque">
                <v-row dense>
                  <v-col cols="12" md="5">
                    <ValidationProvider rules="required|max:40" name="nombre" v-slot="{ errors, valid }">
                      <v-text-field
                        v-model="form.nombre"
                        name="nombre"
                        label="Nombre del perfil"
                        placeholder="Ej: Auditor"
                        outlined
                        dense
                        :error-messages="errors"
                        :success="valid"
                      />
                    </ValidationProvider>
                  </v-col>
                  <v-col cols="12" md="7">
                    <v-text-field
                      v-model="form.descripcion"
                      label="Descripcion"
                      placeholder="Para que sirve este perfil"
                      outlined
                      dense
                    />
                  </v-col>
                </v-row>

                <v-switch
                  v-model="form.activo"
                  :label="form.activo ? 'Perfil activo' : 'Perfil inactivo (no se puede asignar ni usar)'"
                  color="success"
                  dense
                  hide-details
                  class="mt-0"
                />
              </div>

              <!-- Alcance: uno solo para todo el perfil, compartido por los
                   modulos de PQR. Aparece solo si alguno esta habilitado. -->
              <v-expand-transition>
                <div v-if="tieneModulosAcotables" class="bloque alcance-bloque">
                  <div class="seccion-titulo">{{ ALCANCE.titulo }}</div>
                  <div class="seccion-ayuda mb-2">{{ ALCANCE.ayuda }}</div>

                  <v-radio-group
                    :value="alcance"
                    dense
                    hide-details
                    class="ma-0 pa-0 alcance-opciones"
                    @change="cambiarAlcance"
                  >
                    <v-radio :label="ALCANCE.opcionTodas" value="todas"/>
                    <v-radio :label="ALCANCE.opcionAlgunas" value="algunas"/>
                  </v-radio-group>

                  <v-select
                    v-if="alcance === 'algunas'"
                    v-model="form.dependencias"
                    :items="dependencias"
                    item-text="name"
                    item-value="id"
                    placeholder="Elegí una o mas dependencias"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    dense
                    outlined
                    hide-details
                    class="alcance-select mt-2"
                  />
                  <div v-if="alcance === 'algunas' && dependencias.length === 0" class="alcance-aviso">
                    Todavia no hay dependencias cargadas. Se crean en PQR Externos > Dependencias.
                  </div>

                  <div class="alcance-modulos">
                    Aplica a: {{ nombresModulosAcotables }}
                  </div>
                </div>
              </v-expand-transition>

              <!-- Accesos -->
              <div class="seccion-encabezado">
                <div>
                  <div class="seccion-titulo">Accesos</div>
                  <div class="seccion-ayuda">
                    Al habilitar una pantalla se despliegan los permisos que tiene disponibles.
                  </div>
                </div>
                <v-spacer/>
                <v-chip small color="primary" dark class="mr-2">
                  <v-icon x-small left>desktop_windows</v-icon>
                  {{ form.pantallas.length }}
                </v-chip>
                <v-chip small color="teal" dark>
                  <v-icon x-small left>vpn_key</v-icon>
                  {{ form.permisos.length }}
                </v-chip>
              </div>

              <div v-for="grupo in accesosAgrupados" :key="grupo.nombre" class="bloque grupo">
                <div class="grupo-titulo">
                  <v-icon small color="blue-grey lighten-1">{{ grupo.icono }}</v-icon>
                  <span>{{ grupo.nombre }}</span>
                </div>

                <div
                  v-for="m in grupo.items"
                  :key="m.clave"
                  class="modulo"
                  :class="{ activo: form.pantallas.includes(m.clave) }"
                >
                  <v-checkbox
                    v-model="form.pantallas"
                    :value="m.clave"
                    :disabled="m.soloSistema || m.obligatorio"
                    dense
                    hide-details
                    class="ma-0 pa-0 modulo-check"
                  >
                    <template v-slot:label>
                      <span class="modulo-nombre">{{ m.nombre }}</span>
                      <v-chip v-if="m.obligatorio" x-small class="ml-2" color="blue lighten-5" text-color="blue darken-2">
                        siempre habilitada
                      </v-chip>
                      <v-chip v-else-if="m.soloSistema" x-small class="ml-2" color="grey lighten-2">
                        solo Administrador
                      </v-chip>
                      <v-chip
                        v-else-if="m.permisos.length && form.pantallas.includes(m.clave)"
                        x-small
                        class="ml-2"
                        color="teal lighten-5"
                        text-color="teal darken-2"
                      >
                        {{ permisosMarcados(m) }} de {{ m.permisos.length }}
                      </v-chip>
                    </template>
                  </v-checkbox>
                  <!-- Los permisos aparecen recien cuando la pantalla esta habilitada -->
                  <v-expand-transition>
                    <div v-if="form.pantallas.includes(m.clave) && m.permisos.length" class="permisos">
                      <v-checkbox
                        v-for="permiso in m.permisos"
                        :key="permiso.clave"
                        v-model="form.permisos"
                        :value="permiso.clave"
                        dense
                        hide-details
                        class="ma-0 pa-0 permiso"
                      >
                        <template v-slot:label>
                          <div>
                            <div class="permiso-nombre">{{ permiso.nombre }}</div>
                            <div class="permiso-detalle">{{ permiso.detalle }}</div>
                          </div>
                        </template>
                      </v-checkbox>
                    </div>
                  </v-expand-transition>
                </div>
              </div>

            </ValidationObserver>
          </v-card-text>

          <v-divider/>

          <v-card-actions class="dialogo-acciones">
            <v-spacer/>
            <v-btn text :disabled="guardando" @click="dialog = false">Cancelar</v-btn>
            <v-btn color="primary" depressed :loading="guardando" @click="intentarGuardar">
              {{ edit ? 'Guardar cambios' : 'Crear perfil' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Confirmacion de borrado -->
      <v-dialog v-model="deleteDialog" width="480">
        <v-card v-if="toDelete">
          <v-card-title class="headline">Eliminar perfil</v-card-title>
          <v-card-text>
            <p>Vas a eliminar el perfil <strong>{{ toDelete.nombre }}</strong>.</p>
            <v-alert v-if="usuariosConPerfil(toDelete.id) > 0" type="warning" dense text>
              Hay {{ usuariosConPerfil(toDelete.id) }} usuario(s) con este perfil.
              Si lo eliminas, esas personas dejan de poder entrar al panel hasta que
              les asignes otro perfil.
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer/>
            <v-btn text :disabled="borrando" @click="deleteDialog = false">Cancelar</v-btn>
            <v-btn color="error" :loading="borrando" @click="confirmDelete">Eliminar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-snackbar
        :color="snackColor"
        :timeout="snackColor === 'error' ? 8000 : 5000"
        v-model="snackbar"
      >
        {{ text }}
        <template v-slot:action="{ attrs }">
          <v-btn v-bind="attrs" color="white" text @click="snackbar = false">Cerrar</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </v-content>
</template>

<script>
import { mapState } from 'vuex'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import { MODULOS, GRUPOS_MENU, ALCANCE, modulosAcotables, permisosMarcablesDeModulo, permisosSinPantalla, modulosObligatorios, conObligatorias } from '../plugins/permisos'

export default {
  name: 'Perfiles',

  components: { ValidationObserver, ValidationProvider },

  data () {
    return {
      search: '',
      dialog: false,
      deleteDialog: false,
      toDelete: null,
      edit: false,
      editId: null,
      cargando: true,
      guardando: false,
      borrando: false,
      sembrando: false,
      snackbar: false,
      snackColor: 'success',
      text: '',
      ALCANCE,
      form: {
        nombre: '',
        descripcion: '',
        activo: true,
        pantallas: [],
        permisos: [],
        dependencias: []
      },
      headers: [
        { text: 'Perfil', align: 'left', value: 'nombre' },
        { text: 'Descripcion', value: 'descripcion' },
        { text: 'Pantallas', value: 'pantallas', sortable: false },
        { text: 'Permisos', value: 'permisos', sortable: false },
        { text: 'Usuarios', value: 'usuarios', sortable: false },
        { text: 'Estado', value: 'activo' },
        { text: 'Opciones', value: 'actions', sortable: false }
      ]
    }
  },

  computed: {
    ...mapState({
      perfiles: state => state.perfil.perfiles,
      usuarios: state => state.user.users,
      dependencias: state => state.dependencia.dependencias
    }),

    // Modulos acotables que el perfil tiene habilitados. Si no hay ninguno,
    // el alcance no aplica y no se muestra.
    modulosAcotablesActivos () {
      return modulosAcotables().filter(m => this.form.pantallas.includes(m.clave))
    },

    tieneModulosAcotables () {
      return this.modulosAcotablesActivos.length > 0
    },

    nombresModulosAcotables () {
      return this.modulosAcotablesActivos.map(m => m.nombre).join(', ')
    },

    // El alcance es excluyente: o ve todas las dependencias -y eso se guarda
    // como el permiso correspondiente- o ve solo las elegidas. Nunca las dos.
    alcance () {
      return this.form.permisos.includes(ALCANCE.permisoTodas) ? 'todas' : 'algunas'
    },

    // Un solo arbol: grupo -> pantalla -> permisos de esa pantalla. Los permisos
    // dejaron de ser una lista suelta porque solo aplican dentro de su modulo, y
    // verlos separados hacia parecer que servian por si solos.
    accesosAgrupados () {
      const nombres = [...new Set(MODULOS.map(m => m.grupo))]
      const orden = ['General', ...GRUPOS_MENU.map(g => g.nombre)]

      return nombres
        .sort((a, b) => orden.indexOf(a) - orden.indexOf(b))
        .map(nombre => {
          const grupo = GRUPOS_MENU.find(g => g.nombre === nombre)
          return {
            nombre,
            icono: grupo ? grupo.icono : 'widgets',
            items: MODULOS
              .filter(m => m.grupo === nombre)
              .map(m => ({ ...m, permisos: permisosMarcablesDeModulo(m.clave) }))
          }
        })
    }
  },

  // Deshabilitar una pantalla tiene que arrastrar sus permisos: si no, quedan
  // guardados permisos que no aplican a nada y el perfil miente sobre lo que hace.
  watch: {
    'form.pantallas' (pantallas) {
      // Red de seguridad: si por cualquier via quedara fuera una pantalla
      // obligatoria, se vuelve a poner. La casilla ya esta deshabilitada, pero esto
      // garantiza que nunca se guarde un perfil sin ella.
      const faltantes = modulosObligatorios().filter(c => !pantallas.includes(c))
      if (faltantes.length > 0) {
        this.form.pantallas = [...pantallas, ...faltantes]
        return
      }

      const huerfanos = permisosSinPantalla(this.form.permisos, pantallas)
      if (huerfanos.length > 0) {
        this.form.permisos = this.form.permisos.filter(clave => !huerfanos.includes(clave))
      }

      // El alcance solo aplica si queda algun modulo acotable habilitado.
      const acotables = modulosAcotables().filter(m => pantallas.includes(m.clave))
      if (acotables.length === 0) {
        if (this.form.dependencias.length > 0) this.form.dependencias = []
        this.form.permisos = this.form.permisos.filter(p => p !== ALCANCE.permisoTodas)
      }
    }
  },

  async mounted () {
    // El conteo de usuarios por perfil no bloquea la pantalla.
    this.$store.dispatch('bindUsers')
    // Hacen falta para el selector de alcance de PQR Externos.
    this.$store.dispatch('dependencia/bindDependencias')
    try {
      await this.$store.dispatch('perfil/bindPerfiles')
    } catch (e) {
      this.notify('No se pudieron cargar los perfiles. Revisa la conexion o los permisos.', 'error')
    } finally {
      this.cargando = false
    }
  },

  methods: {
    cambiarAlcance (valor) {
      const clave = ALCANCE.permisoTodas

      if (valor === 'todas') {
        if (!this.form.permisos.includes(clave)) {
          this.form.permisos = [...this.form.permisos, clave]
        }
        this.form.dependencias = []
      } else {
        this.form.permisos = this.form.permisos.filter(p => p !== clave)
      }
    },

    permisosMarcados (modulo) {
      return modulo.permisos.filter(p => this.form.permisos.includes(p.clave)).length
    },

    usuariosConPerfil (perfilId) {
      return (this.usuarios || []).filter(u => u.perfilId === perfilId).length
    },

    async sembrar () {
      this.sembrando = true
      try {
        const creados = await this.$store.dispatch('perfil/sembrarPerfiles')
        this.notify(
          creados > 0 ? 'Perfiles iniciales creados' : 'Ya existian perfiles cargados',
          'success'
        )
      } catch (e) {
        this.notify('Error al crear los perfiles iniciales', 'error')
      } finally {
        this.sembrando = false
      }
    },

    openModal () {
      this.edit = false
      this.editId = null
      // Las pantallas obligatorias vienen marcadas desde el principio.
      this.form = { nombre: '', descripcion: '', activo: true, pantallas: modulosObligatorios(), permisos: [], dependencias: [] }
      this.dialog = true
      this.resetValidation()
    },

    editItem (item) {
      if (item.sistema) return
      this.edit = true
      this.editId = item.id
      this.form = {
        nombre: item.nombre || '',
        descripcion: item.descripcion || '',
        activo: item.activo !== false,
        // Un perfil guardado antes de que existiera esta pantalla no la tiene en su
        // lista; se agrega al abrir el formulario y queda guardada al aceptar.
        pantallas: conObligatorias(item.pantallas),
        permisos: [...(item.permisos || [])],
        dependencias: [...(item.dependencias || [])]
      }
      this.dialog = true
      this.resetValidation()
    },

    resetValidation () {
      this.$nextTick(() => {
        if (this.$refs.obs) this.$refs.obs.reset()
      })
    },

    deleteItem (item) {
      if (item.sistema) return
      // Borrar el perfil con el que uno esta operando deja al usuario afuera
      // del panel sin forma de volver a entrar.
      if (item.id === this.miPerfilId) {
        this.notify('No podes eliminar el perfil que estas usando vos mismo.', 'error')
        return
      }
      this.toDelete = item
      this.deleteDialog = true
    },

    async confirmDelete () {
      // El dialogo se puede confirmar dos veces antes de cerrarse.
      if (!this.toDelete || this.borrando) return

      this.borrando = true
      try {
        await this.$store.dispatch('perfil/deletePerfil', this.toDelete.id)
        this.notify('Perfil eliminado', 'success')
        this.deleteDialog = false
        this.toDelete = null
      } catch (e) {
        this.notify('Error al eliminar el perfil', 'error')
      } finally {
        this.borrando = false
      }
    },

    // El ValidationObserver ya no envuelve la v-card, asi que se valida por ref.
    async intentarGuardar () {
      const valido = await this.$refs.obs.validate()
      if (!valido) return
      await this.formSubmit()
    },

    async formSubmit () {
      const nombre = (this.form.nombre || '').trim()
      if (!nombre) return

      const duplicado = this.perfiles.some(
        p => p.id !== this.editId && (p.nombre || '').trim().toLowerCase() === nombre.toLowerCase()
      )
      if (duplicado) {
        this.notify('Ya existe un perfil con ese nombre', 'error')
        return
      }

      // Un perfil sin pantallas deja al usuario sin ningun destino: el router lo
      // detecta y le cierra la sesion. Mejor no dejar guardarlo asi.
      // Un perfil acotado sin ninguna dependencia elegida no ve absolutamente
      // nada, y desde la pantalla no habria forma de darse cuenta.
      const sinAlcance = this.tieneModulosAcotables &&
        this.alcance === 'algunas' &&
        this.form.dependencias.length === 0
      if (sinAlcance) {
        this.notify('Elegí al menos una dependencia, o pasá el alcance a "Todas".', 'error')
        return
      }

      if (this.form.pantallas.length === 0) {
        this.notify('El perfil tiene que tener al menos una pantalla habilitada.', 'error')
        return
      }

      // No se puede desactivar el perfil con el que uno esta operando: al
      // recargar quedaria sin sesion. Administrar usuarios y perfiles ya es
      // exclusivo del perfil de sistema, que no es editable, asi que no hace
      // falta proteger esas pantallas aca.
      if (this.edit && this.editId === this.miPerfilId && !this.form.activo) {
        this.notify('No podes desactivar el perfil que estas usando.', 'error')
        return
      }

      const data = {
        nombre,
        descripcion: (this.form.descripcion || '').trim(),
        activo: this.form.activo,
        pantallas: this.form.pantallas,
        permisos: this.form.permisos,
        dependencias: this.form.dependencias
      }

      this.guardando = true
      try {
        if (this.edit) {
          await this.$store.dispatch('perfil/updatePerfil', { id: this.editId, data })
          this.notify('Perfil actualizado', 'success')
        } else {
          await this.$store.dispatch('perfil/createPerfil', data)
          this.notify('Perfil creado', 'success')
        }
        this.dialog = false
      } catch (e) {
        this.notify('Error al guardar el perfil', 'error')
      } finally {
        this.guardando = false
      }
    },

    notify (message, color) {
      this.text = message
      this.snackColor = color
      this.snackbar = true
    }
  }
}
</script>

<style scoped>
/* El dialogo scrollable de Vuetify exige que la v-card sea hijo directo del
   v-dialog: por eso el ValidationObserver quedo adentro de la card y no
   envolviendola, que era lo que dejaba el fondo transparente abajo. */
.dialogo-perfil {
  display: flex;
  flex-direction: column;
}

.dialogo-encabezado {
  display: flex;
  align-items: center;
  padding: 16px 24px;
  background: #1565c0;
  color: #fff;
}

.dialogo-titulo {
  font-size: 18px;
  font-weight: 600;
  line-height: 1.2;
}

.dialogo-subtitulo {
  font-size: 12px;
  opacity: 0.75;
  font-weight: 400;
  line-height: 1.3;
  margin-top: 2px;
}

.dialogo-cuerpo {
  max-height: 66vh;
  background: #eef1f5;
  padding: 20px 24px 24px !important;
}

.dialogo-acciones {
  padding: 12px 20px;
  background: #fff;
}

/* Tarjetas de contenido sobre el fondo gris */
.bloque {
  background: #fff;
  border-radius: 8px;
  padding: 18px 20px;
  margin-bottom: 14px;
  box-shadow: 0 1px 3px rgba(38, 50, 56, 0.1);
}

.seccion-encabezado {
  display: flex;
  align-items: center;
  margin: 22px 2px 12px;
}

.seccion-titulo {
  font-size: 15px;
  font-weight: 600;
  color: #263238;
}

.seccion-ayuda {
  font-size: 12px;
  color: #78909c;
  margin-top: 1px;
}

.grupo {
  padding: 14px 20px 10px;
}

.grupo-titulo {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.9px;
  text-transform: uppercase;
  color: #90a4ae;
  padding-bottom: 10px;
  border-bottom: 1px solid #eceff1;
}

/* Cada pantalla; al habilitarla se resalta y despliega sus permisos */
.modulo {
  padding: 10px 12px;
  margin: 6px -12px 0;
  border-radius: 6px;
  border-left: 3px solid transparent;
  transition: background-color 0.18s, border-color 0.18s;
}

.modulo:hover {
  background: #f7f9fb;
}

.modulo.activo {
  background: #f5fbfa;
  border-left-color: #26a69a;
}

.modulo-check >>> .v-label {
  display: flex;
  align-items: center;
}

.modulo-nombre {
  font-size: 14px;
  font-weight: 500;
  color: #37474f;
}

.permisos {
  margin: 4px 0 4px 32px;
  padding: 8px 0 4px 16px;
  border-left: 2px solid #b2dfdb;
}

.permiso {
  padding: 6px 0 !important;
}

.permiso-nombre {
  font-size: 13px;
  color: #455a64;
  line-height: 1.35;
}

.permisos-subtitulo {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  color: #90a4ae;
  margin-bottom: 2px;
}

.alcance-bloque {
  border-left: 3px solid #26a69a;
}

.alcance-modulos {
  font-size: 11px;
  color: #90a4ae;
  margin-top: 8px;
}

.alcance-opciones {
  margin-bottom: 6px !important;
}

.alcance-opciones >>> .v-label {
  font-size: 13px;
  color: #455a64;
}

.alcance-select {
  max-width: 460px;
  margin-bottom: 4px;
}

.alcance-aviso {
  font-size: 11px;
  font-style: italic;
  color: #ef9a9a;
  padding: 2px 0 4px;
}

.permiso-detalle {
  font-size: 11px;
  color: #90a4ae;
  line-height: 1.4;
  max-width: 640px;
  margin-top: 1px;
}
</style>
